import { DialogContainer } from "$components/dialog/dialog-container";
import { CloseIcon } from "$components/icons";
import {
  CircularProgress,
  DialogTitle,
  IconButton,
  styled,
} from "@material-ui/core";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { ImagingStudyWithImages } from "./use-patient-imaging-studies";

const StyledDialog = styled(DialogContainer)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: 960,
    width: "90vw",
    margin: theme.spacing(1),
    borderRadius: 16,
    padding: theme.spacing(2),
    position: "relative", // Enable absolute positioning for children
    [theme.breakpoints.up("sm")]: {
      margin: theme.spacing(2),
      padding: theme.spacing(3),
    },
  },
}));

// TODO WEB-5418 - Move to global components?
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(2),
  padding: 0, // Paper already provides padding
}));

const MainImageContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "60vh",
  maxHeight: 640,
  marginBottom: theme.spacing(3), // 24px gap to thumbnails
}));

const MainImage = styled("img")(({ theme }) => ({
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
  borderRadius: 4,
}));

const MainImagePlaceholder = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.grey[100],
  borderRadius: 4,
}));

const ThumbnailGallery = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  justifyContent: "center",
  flexWrap: "wrap",
  paddingBottom: theme.spacing(2),
}));

const ThumbnailContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: 80,
  height: 80,
  borderRadius: 4,
  cursor: "pointer",
  boxSizing: "border-box",
}));

const ThumbnailImage = styled("img")(({ theme }) => ({
  width: 80,
  height: 80,
  objectFit: "cover",
  borderRadius: 4,
}));

const SelectedThumbnailContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: 92, // 80px image + 2px white border × 2 + 4px blue border × 2
  height: 92, // 80px image + 2px white border × 2 + 4px blue border × 2
  borderRadius: 8,
  cursor: "pointer",
  boxSizing: "border-box",
  border: `4px solid ${theme.palette.primary.main}`,
  padding: 0,
  margin: -6, // Negative margin to prevent layout shift
}));

const SelectedThumbnailImage = styled("img")(({ theme }) => ({
  width: 80,
  height: 80,
  objectFit: "cover",
  borderRadius: 4,
  border: `2px solid ${theme.palette.common.white}`,
  boxSizing: "content-box", // Border adds to the dimensions, not subtracts
}));

const ThumbnailPlaceholder = styled("div")(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
}));

const SelectedThumbnailPlaceholder = styled("div")(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  border: `2px solid ${theme.palette.common.white}`,
  boxSizing: "content-box",
}));

export const ImagingGalleryModal: FC<{
  open: boolean;
  images: ImagingStudyWithImages["images"];
  initialImageIndex: number;
  onClose: () => void;
}> = ({ open, images, initialImageIndex, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState(initialImageIndex);

  useEffect(() => {
    setSelectedIndex(initialImageIndex);
  }, [initialImageIndex]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
          break;
        case "ArrowRight":
          event.preventDefault();
          setSelectedIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
          break;
        case "Escape":
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, images.length, onClose]);

  if (!open || images.length === 0) {
    return null;
  }

  const selectedImage = images[selectedIndex];

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      data-testid="imaging-gallery-modal"
      aria-label="Imaging gallery"
    >
      <StyledDialogTitle disableTypography>
        <IconButton
          aria-label="close"
          data-testid="Modal.closeIcon"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <MainImageContainer>
        {selectedImage?.previewLoading ? (
          <MainImagePlaceholder>
            <CircularProgress size={48} />
          </MainImagePlaceholder>
        ) : selectedImage?.previewUrl ? (
          <MainImage
            src={selectedImage.previewUrl}
            alt={`Imaging instance ${selectedImage.id}`}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <MainImagePlaceholder>
            {/* Empty placeholder for failed loads */}
          </MainImagePlaceholder>
        )}
      </MainImageContainer>

      <ThumbnailGallery>
        {images.map((image, index) => {
          const isSelected = index === selectedIndex;
          const thumbnailUrl = image.thumbnailUrl || image.previewUrl;
          const isLoading =
            image.thumbnailLoading ||
            (image.previewLoading && !image.thumbnailUrl);

          if (isLoading) {
            const Container = isSelected
              ? SelectedThumbnailContainer
              : ThumbnailContainer;
            const Placeholder = isSelected
              ? SelectedThumbnailPlaceholder
              : ThumbnailPlaceholder;

            return (
              <Container key={image.id} onClick={() => setSelectedIndex(index)}>
                <Placeholder>
                  <CircularProgress size={24} />
                </Placeholder>
              </Container>
            );
          }

          if (!thumbnailUrl) {
            const Container = isSelected
              ? SelectedThumbnailContainer
              : ThumbnailContainer;
            const Placeholder = isSelected
              ? SelectedThumbnailPlaceholder
              : ThumbnailPlaceholder;

            return (
              <Container key={image.id} onClick={() => setSelectedIndex(index)}>
                <Placeholder>
                  {/* Empty placeholder for failed loads */}
                </Placeholder>
              </Container>
            );
          }

          const Container = isSelected
            ? SelectedThumbnailContainer
            : ThumbnailContainer;
          const Image = isSelected ? SelectedThumbnailImage : ThumbnailImage;

          return (
            <Container key={image.id} onClick={() => setSelectedIndex(index)}>
              <Image
                src={thumbnailUrl}
                alt={`Imaging instance ${image.id}`}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </Container>
          );
        })}
      </ThumbnailGallery>
    </StyledDialog>
  );
};
